import { useMemo, useState } from 'react';
import { useEditor } from '../../state/store';
import { CATEGORIES, charName, searchChars } from '../../unicode/charsets';
import { analyzeChar } from '../../unicode/width';
import { Panel } from '../widgets/common';
import { Select } from '../widgets/primitives';

/** Searchable Unicode browser feeding the brush character. */
export function CharPalettePanel(): JSX.Element {
  const brush = useEditor((s) => s.brush);
  const setBrush = useEditor((s) => s.setBrush);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('box');

  const chars = useMemo(() => {
    if (query.trim()) return searchChars(query, 300).map((c) => c.char);
    return CATEGORIES.find((c) => c.id === category)?.chars ?? [];
  }, [query, category]);

  const warning = analyzeChar(brush.char);

  return (
    <Panel title="Characters">
      <input
        className="input"
        placeholder="Search name, char or U+2500…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 6 }}
      />
      {!query.trim() && (
        <div style={{ marginBottom: 6 }}>
          <Select
            ariaLabel="Character category"
            value={category}
            options={CATEGORIES.map((c) => ({
              value: c.id,
              label: c.name,
              hint: String(c.chars.length),
            }))}
            onChange={setCategory}
          />
        </div>
      )}

      <div className="char-grid">
        {chars.map((ch, i) => (
          <button
            key={`${ch}-${i}`}
            className={`char-cell${brush.char === ch ? ' selected' : ''}`}
            title={`${charName(ch)}  U+${(ch.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, '0')}`}
            onClick={() => setBrush({ char: ch })}
          >
            {ch}
          </button>
        ))}
        {chars.length === 0 ? <div className="empty-state">No characters match.</div> : null}
      </div>

      <div style={{ marginTop: 8 }}>
        <div className="row">
          <span className="mono" style={{ fontSize: 18, width: 26, textAlign: 'center' }}>
            {brush.char}
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="dim" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {charName(brush.char)}
            </div>
            <div className="faint mono">
              U+{(brush.char.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, '0')}
            </div>
          </div>
        </div>
        {warning ? (
          <div className="warn-text" style={{ marginTop: 4 }}>
            ⚠ {warning.message}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
